<template>
  <div
    class="editable-label editable-label-radio clear-float"
    :style="disabled ? '' : 'cursor: pointer;'"
    @click="editInput"
  >
    <div v-show="legend != ''" class="legend legend--bottom-margin">
      {{ legend }}
    </div>

    <div
      v-show="!noText"
      class="editable-label-text"
      :class="{ 'generic-border--thin-border generic-border--clickable': genericBorder }"
    >
      <div :placeholder="decodeHTML(placeholder)">{{ decodeHTML(textValue) }}</div>
    </div>

    <!-- Selectable options -->
    <transition name="fade" mode="out-in">
      <div
        v-if="editing"
        ref="editionModal"
        class="dark-overlay v-centerer editable-label-radio__modal"
        @click="stopEdit($event, true)"
      >
        <div class="dark-overlay-content" :class="{ large: itemsAreSplit }" @click.stop="">

          <!-- Options split into multiple categories => categories appear on the left -->
          <div
            class="editable-label-radio__edition"
            :class="{ 'has-hint': hasHint, 'has-categories': itemsAreSplit, 'has-ok-btn': okButton }"
          >
            <div class="editable-label-radio__edition__wrapper">
              <!-- Option categories -->
              <div class="editable-label-radio__edition__left-side v-centerer f-h">

                <div v-if="hasHint" class="editable-label-radio__edition__left-side__hint field-hint" v-html="hint">
                  <!-- <div class="editable-label-radio__edition__left-side__hint__content" v-html="hint" /> -->
                </div>

                <div v-if="itemsAreSplit" class="editable-label-radio__edition__left-side__content">
                  <div v-if="categoryLevel === 2">
                    <div v-for="(category, catIndex) in items" :key="catIndex">
                      <div
                        class="editable-label-radio__edition__category-label generic-border"
                        :class="{ selected: currentCategory == catIndex }"
                        @click="selectCategory(catIndex)"
                      >
                        {{ decodeHTML(category.title) }}
                      </div>
                      <div
                        v-for="(subcategory, subcatIndex) in category.radioItems"
                        :key="subcatIndex"
                        class="editable-label-radio__edition__category-label--lv2"
                        :class="{ selected: currentSubCategory == subcatIndex }"
                        @click="selectCategory(catIndex, subcatIndex)"
                      >
                        {{ decodeHTML(subcategory.title) }}
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <div
                      v-for="(category, catIndex) in items"
                      :key="catIndex"
                      class="editable-label-radio__edition__category-label generic-border"
                      :class="{selected: currentCategory == catIndex}"
                      @click="selectCategory(catIndex)"
                    >
                      {{ decodeHTML(category.title) }}
                    </div>
                  </div>
                </div>
              </div>
              <!-- Selectable options split into categories -->
              <div ref="values" class="editable-label-radio__edition__values" @scroll="scrollItems">
                <div class="v-centerer">
                  <div class="
                    editable-label-radio__edition__values__search
                    v-centerer
                    generic-border
                    generic-border--focus-shadow
                  "
                  >
                    <Icon>{{ searchMedium }}</Icon>
                    <input v-model="search" :placeholder="$t('message.FILTER_RADIO_ITEMS')">
                  </div>
                  <CloseButton :absolute="false" @click="stopEdit($event, true)" />
                </div>
                <div v-if="itemsAreSplit" class="editable-label-radio__edition__values__wrapper">
                  <!-- Two levels of categories -->
                  <div v-if="categoryLevel === 2">
                    <div
                      v-for="(category, catIndex) in filteredItems"
                      :ref="`category${catIndex}`"
                      :key="catIndex"
                      class="editable-label-radio__edition__values__category"
                    >
                      <div class="editable-label-radio__edition__values__category__title">
                        {{ decodeHTML(category.title) }}
                      </div>
                      <div v-if="category.radioItems.length === 0 && noneString !== ''" class="field-hint">
                        {{ noneString }}
                      </div>
                      <div
                        v-for="(subcategory, subcatIndex) in category.radioItems"
                        :ref="`subcategory${catIndex}-${subcatIndex}`"
                        :key="subcatIndex"
                        class="editable-label-radio__edition__values__category--lv2"
                      >
                        <div class="editable-label-radio__edition__values__category__title--lv2">
                          <span>
                            {{ decodeHTML(subcategory.title) }}
                          </span>
                          <span class="editable-label-radio__edition__values__category__title__decoration--lv2" />
                        </div>
                        <div v-if="subcategory.radioItems.length === 0 && noneString !== ''" class="field-hint">
                          {{ noneString }}
                        </div>
                        <!-- eslint-disable max-len -->
                        <Checkbox
                          v-for="(item, index) in subcategory.radioItems"
                          :key="index"
                          class="editable-label-radio__edition__values__item generic-border generic-border--clickable"
                          :value="isSelected(item)"
                          :title="decodeHTML(item[labelKey])"
                          @input="selectItem(item, $event)"
                        />
                      </div>
                    </div>
                  </div>
                  <!-- One level of categories -->
                  <div v-else>
                    <div
                      v-for="(category, catIndex) in filteredItems"
                      :ref="`category${catIndex}`"
                      :key="catIndex"
                      class="editable-label-radio__edition__values__category"
                    >
                      <div v-if="category.radioItems.length === 0 && noneString !== ''" class="field-hint">
                        {{ noneString }}
                      </div>
                      <ExpandableSettingsItem
                        :title="`${decodeHTML(category.title)} (${category.radioItems.length})`"
                        :default-expand="category.radioItems.length < 50 && catIndex < 10"
                        use-transition-expand
                      >
                        <div>
                          <div class="generic-background">
                            <Checkbox
                              v-for="(item, index) in category.radioItems"
                              :key="index"
                              class="editable-label-radio__edition__values__item generic-border generic-border--clickable"
                              :value="isSelected(item)"
                              :title="decodeHTML(item[labelKey])"
                              @input="selectItem(item, $event)"
                            />
                          </div>
                        </div>
                      </ExpandableSettingsItem>
                    </div>
                  </div>
                </div>
                <!-- Simple list of options -->
                <div v-else class="editable-label-radio__edition__values__wrapper">
                  <div class="label-radio-category">
                    <div v-if="items.length === 0 && noneString !== ''" class="field-hint">
                      {{ noneString }}
                    </div>
                    <Checkbox
                      v-for="(item, index) in filteredItems"
                      :key="index"
                      class="editable-label-radio__edition__values__item generic-border generic-border--clickable"
                      :value="isSelected(item)"
                      :title="decodeHTML(item[labelKey])"
                      @click.native="selectItem(item, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div v-if="okButton" class="h-v-centerer editable-label-radio__modal__ok-btn-wrapper">
              <button class="generic-border generic-border--text-btn generic-border--blue" @click="$emit('ok')">
                {{ $t('message.OK') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * Component to edit some data using a list of items to pick from
 *
 * @vue-prop {String} legend - String to use as a legend of the value (empty for no legend)
 * @vue-prop {Number|Array} v-model - Id of the value picked in the list of items
 * @vue-prop {Array} items - This component can be used to select values from a simple list or to select
 *                           values from multiple lists. For instance, select a user among the current team's users.
 *                           Or select a user accros multiple teams' users.
 *                           When setting up this component for multiple categories, items should be formed as:
 *                           { title: 'Category title', radioItems: [ { idKey:12, labelKey: 'Option n°12' }, ... ] }
 *                           When setting up this component without categories, items should be formed as:
 *                           { idKey:12, labelKey: 'Option n°12' }
 *
 * @vue-prop {String} labelKey - key of the object property that should be used as a label
 * @vue-prop {String} idKey - key of the object property that should be used as an identifier
 * @vue-prop {String} placeholder - Text to use as an HTML placeholder attr
 * @vue-prop {String} separator - Text to use as a separator when multiple is true
 * @vue-prop {String} hint - Help text
 * @vue-prop {String} noneString - String to displayed when there's no option to select in the list
 * @vue-prop {Number} categoryLevel - No categories: 0, categories: 1, categories and sub-cat: 2, etc.
 * @vue-prop {Boolean} multiple - Allow picking multiple items. Thus v-model will be an array of numbers
 * @vue-prop {Boolean} disabled - Prevent edition
 * @vue-prop {Boolean} noText - Hide the text value
 * @vue-prop {Boolean} okButton - Display an 'OK' button that will $emit 'ok' when clicked
 * @vue-prop {Boolean} ellipsize - Whether the displayed text should be ellipsized (needs a height CSS attr to work)
 * @vue-prop {Boolean} stopPropagation - Prevent the click event propagation when clicking on buttons
 * @vue-prop {Boolean} modalInComponent - The default behavior is to add the modal to the document body to prevent
 *                                   conflicting z indexes. But this can be avoided by setting modalInComponent to true
 *
 * @vue-event {Number|Array} input - Emits the edited value when an item is picked. If multipl is true, emits an array of numbers
 * @vue-event {} ok - OK button clicked
 * @vue-event {} close - Edition popup was closed
 *
 */

import Checkbox from '@/components/checkbox.vue';
import CloseButton from '@/components/close-button.vue';
import EllipsisDiv from '@/components/ellipsize.vue';
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import Icon from '@/components/icon.vue';

import { detectEscapeKey, decodeHTML } from '@/utils/main';
import { searchMedium, tickExtraBold } from '@/assets/icons';

export default {

  name: 'EditableLabelRadio',

  components: {
    Checkbox,
    CloseButton,
    EllipsisDiv,
    ExpandableSettingsItem,
    Icon,
  },

  props: {
    legend: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number, Array],
      default: 0,
    },
    items: {
      type: Array,
      default: () => [],
    },
    labelKey: {
      type: String,
      default: 'label',
    },
    idKey: {
      type: String,
      default: 'id',
    },
    placeholder: {
      type: String,
      default: '',
    },
    separator: {
      type: String,
      default: ', ',
    },
    hint: {
      type: String,
      default: '',
    },
    noneString: {
      type: String,
      default: '',
    },
    categoryLevel: {
      type: Number,
      default: 0,
    },
    multiple: Boolean,
    disabled: Boolean,
    noText: Boolean,
    okButton: Boolean,
    ellipsize: Boolean,
    genericBorder: Boolean,
    stopPropagation: Boolean,
    modalInComponent: Boolean,
  },

  data() {
    return {
      searchMedium,
      tickExtraBold,
      editing: false,
      currentCategory: 0,
      currentSubCategory: 0,
      disableScrollHandler: false,
      search: '',
    };
  },

  computed: {
    /** Boolean: true if items should be split into categories (see component doc for more info) */
    itemsAreSplit() {
      return this.items.length && this.items[0].radioItems && this.items[0].radioItems instanceof Array;
    },
    /** Number: Number of category levels */
    levelsNumber() {
      if (this.categoryLevel > 0) {
        return this.categoryLevel;
      }
      return this.itemsAreSplit ? 1 : 0;
    },
    /** Array: All itemsand categories matching the search terms */
    filteredItems() {
      return this.getFilteredItems(this.items, 0);
    },
    /** Array: All items that can be selected */
    allItems() {
      return this.recursiveGetItems(this.items, 0);
    },
    /** String: String to display the selected value(s) */
    textValue() {
      const { idKey, labelKey } = this;
      if (this.multiple && this.value) {
        let items = this.allItems.filter(item => this.value.includes(item[idKey]));
        const ids = items.map(item => item[this.idKey]);
        items = items.filter((item, idx) => ids.indexOf(item[idKey]) === idx);
        return items.map(item => item[labelKey]).join(this.separator);
      }
      const item = this.allItems.find(item => this.value == item[idKey]);
      return item ? item[labelKey] : '';
    },
    /** Boolean: Whether a hint should be displayed */
    hasHint() {
      return this.hint !== '';
    },
  },

  watch: {
    editing(newVal, oldVal) {
      if (newVal && !oldVal && !this.modalInComponent) {
        this.$nextTick(this.addModalToBody);
      }
    },
  },

  beforeDestroy() {
    if (this.$refs.editionModal) {
      this.$refs.editionModal.remove();
    }
  },

  methods: {
    decodeHTML,
    /**
     * Recursively fetch all items that can be selected in the list of options
     * @param  {Array} radioItems - Array categories/items
     * @param  {Number} level     - level of recursivity
     * @return {Array}            - Array of items
     */
    recursiveGetItems(radioItems, level) {
      if (level === this.levelsNumber) {
        return radioItems;
      }
      if (level < this.levelsNumber) {
        return this.recursiveGetItems(radioItems.flatMap(item => item.radioItems), level + 1);
      }
    },
    /**
     * Recursively build the 'items' prop but where all the items and categories are filtered using the 'search' data
     * @param  {Array} radioItems - Array categories/items
     * @param  {Number} level     - level of recursivity
     * @return {Array}            - Array with the same structure as this.items
     */
    getFilteredItems(radioItems, level) {
      if (level === this.levelsNumber) {
        return radioItems.filter(item => this.searchFilter(item[this.labelKey]));
      }
      if (level < this.levelsNumber) {
        return radioItems
          .map((category) => {
            const matchesSearch = this.searchFilter(category.title);
            return matchesSearch
              ? category
              : { ...category, radioItems: this.getFilteredItems(category.radioItems, level + 1) };
          })
          .filter(category => category.radioItems.length > 0 || this.searchFilter(category.title));
      }
    },
    /**
     * Function used to filter the items by check their name against this.search
     * @param  {String} text - Text to check
     * @return {Boolean}     - True if the item should be kept
     */
    searchFilter(text) {
      const effectiveSearch = this.search.sAccentless.toUpperCase().split(/[\s,-.\/\']/);
      return !effectiveSearch.some(term => !text.sAccentless.toUpperCase().includes(term));
    },
    /** Detect scrolling when multiple categories */
    scrollItems(e) {
      if (this.disableScrollHandler) {
        return;
      }
      const elt = e.srcElement;
      if (elt && this.itemsAreSplit) {
        if (elt.scrollTop == 0) {
          this.currentCategory = 0;
        } else if (elt.scrollTop == (elt.scrollHeight - elt.offsetHeight)) {
          this.currentCategory = this.items.length - 1;
        } else {
          let i;
          for (i = 0; i < this.items.length; i++) {
            const [el] = this.$refs[`category${i}`];
            if (el && el.offsetTop > (elt.scrollTop + elt.offsetHeight / 2)) {
              break;
            }
          }
          this.currentCategory = i - 1;
        }
      }
    },
    /**
     * Scroll to category and select it on the left side of the component
     * @param  {Number} index         Index of the category to select
     * @param  {Number} subcatIndex   If set, index of the sucategory that was clicked
     */
    selectCategory(index, subcatIndex) {
      const { values } = this.$refs;
      const el = subcatIndex !== undefined
        ? this.$refs[`subcategory${index}-${subcatIndex}`][0]
        : this.$refs[`category${index}`][0];
      values.scrollTop = el.offsetTop - values.offsetHeight / 2 || 0;
      this.currentCategory = index;
      // Prevent triggering scroll handler to prevent it from selecting another category
      // NOTE: Need setTimeout instead of $nextTick since next tick handler is triggered right away
      this.disableScrollHandler = true;
      setTimeout(() => { this.disableScrollHandler = false; }, 1);
    },
    /** Add the edition modal to the document body tp prevent zIndex conflicts */
    addModalToBody() {
      this.documentBody.appendChild(this.$refs.editionModal);
    },
    isSelected(item) {
      if (this.multiple) {
        for (let i = 0, len = this.value.length; i < len; i++) {
          if (this.value[i] == item[this.idKey]) {
            return true;
          }
        }
        return false;
      }
      return this.value == item[this.idKey];
    },
    selectItem(item, evt) {
      if (this.multiple) {
        const input = this.value;
        for (let i = 0, len = input.length; i < len; i++) {
          if (input[i] == item[this.idKey]) {
            input.splice(i, 1);
            this.$emit('input', input);
            return;
          }
        }
        input.push(item[this.idKey]);
        this.$emit('input', input);
      } else {
        this.$emit('input', item[this.idKey]);
        this.stopEdit(evt);
      }
    },
    edit() {
      this.editing = true;
      detectEscapeKey(() => {
        this.stopEdit(null, true);
      });
      this.$emit('startEdit');
    },
    stopEdit(evt, closeOrEsc) {
      this.editing = false;
      if (evt && evt.stopPropagation) {
        evt.stopPropagation();
      }
      if (closeOrEsc) {
        this.$emit('close');
      }
    },
    editInput(event) {
      if (!this.stopPropagation) {
        event.stopPropagation();
      }
      if (this.disabled) {
        this.$emit('click', event);
      } else {
        this.edit();
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

// NOTE: Classes used in EditableLabelRadio and ProblemReporting
.label-radio-item {
  padding: 5px 10px;
  background: #dddddd;
  border-radius: 6px;
  margin: 10px 0px;
  cursor: pointer;

  &.selected {
    background: #8ee49c;
  }
  .icon {
    width: 1.4em;
    margin-right: 1em;
    flex: 0 0 auto;
    color: #888888;

    &.selected {
      color: $color-green-good;
    }
  }
  span {
    color: #888888;
    text-align: left;
    flex: 1;
    white-space: pre-line;
  }
  &.selected span {
    color: #2c8f44;
  }
}

.editable-label-radio {
  &__modal {
    &.dark-overlay .dark-overlay-content {
      padding: 0px;
      cursor: initial;
      min-width: 65%;

      &.large {
        height: 75%;
      }

      .editable-label-radio__edition {
        height: 100%;
        display: flex;
        flex-direction: column;
        background: $color-body-background;

        &__wrapper {
          overflow-y: auto;
          display: flex;
          flex: 1;
        }

        &__left-side {
          flex: 0 0 30%;
          box-sizing: border-box;
          display: flex;
          flex-flow: column;

          &__hint {
            flex: 0 0 30%;
            box-sizing: border-box;
            overflow: auto;
            text-align: left;
            padding: .7rem;
          }

          &__content {
            flex: 1;
            overflow: auto;
            text-align: left;
            max-height: 100%;
            min-width: 70%;

            > div {
              padding: 40px 10px 20px 10px;
            }
          }
        }

        &__category-label {
          color: #2A5A91;
          font-weight: 600;
          margin-top: .3em;
          cursor: pointer;
          padding: .3rem .5rem;

          &.generic-border {
            border-radius: $medium-border-radius;
          }

          &.selected {
            background-color: $main-selection-color;
          }

          &:hover {
            background-color: $main-hover-color;
          }

          &--lv2 {
            margin-left: 0.5em;
            min-height: 1.4em;
          }
        }

        &__values {
          box-sizing: border-box;
          flex: 0 0 70%;
          overflow: auto;
          color: $color-shizen-blue !important;

          &__wrapper {
            padding: .5em;
          }

          &__category {

            .settings-dropdown, .expandable-settings-item {
              .settings-item {
                background: none;
                font-weight: bold;
                height: 2.5rem;
              }

              .sd-overflow {
                > div {
                  padding: $page-content-padding;
                }

                .generic-background {
                  padding: $page-content-padding;
                }
              }
            }

            &__title {
              font-size: 1.2em;
              font-weight: 600;
              margin-top: 15px;

              &--lv2 {
                position: relative;
                padding-left: 0.5em;
                padding-bottom: 0.2em;
                min-height: 1.4em;
              }

              &__decoration--lv2 {
                position: absolute;
                bottom: 0px;
                left: 0px;
                top: 0.3em;
                width: 5em;
                border-left: 1px solid $color-light-blue-text;
                border-bottom: 1px solid $color-light-blue-text;
              }
            }
          }

          .close-button {
            margin-right: .5rem;
          }

          &__search {
            margin: .5rem;
            height: $main-component-height;
            flex: 1;

            &.generic-border--thin-border {
              padding: 0px;
            }

            .icon {
              width: $main-component-height;
              height: $main-component-height;
              padding: .8rem;
              box-sizing: border-box;
            }

            input {
              flex: 1;
              margin-right: .5rem;
              padding: 0.5em;
              border: none;
              border-radius: $main-border-radius;
              width: 100%;
              min-width: 0px;
              box-sizing: border-box;
              // Outline done generic-border--focus-shadow
              outline: none;
            }
          }

          &__item {
            height: $main-component-height;
            width: 100%;
            padding: 0 1rem;
            box-sizing: border-box;

            &:not(:first-child) {
              margin-top: .5rem;
            }
          }
        }

        &.has-categories {
          .editable-label-radio__edition__values {
            flex: 0 0 70%;

            &__wrapper {
              position: relative;
              padding: .5rem .5rem;
            }
          }
        }
      }
    }

    &__ok-btn-wrapper {
      flex: 0 0 auto;
      margin: 0.5em auto;
    }
  }
}
</style>
